import { Box, CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import CategoryModal from "./components/CategoryModal";
import SocialCategoryCard from "./components/SocialCategoryCard";
import useSocialInsights from "./hook/useSocialInsights";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import GoogleIcon from "../../icons/GoogleIcon";
import MetaIcon from "../../icons/MetaIcon";
import TiktokIcon from "../../icons/TikTokIcon";
import YoutubeIcon from "../../icons/YoutubeIcon";
import { SocialInsight } from "../../types/leads.types";
import ErrorBoundaryPage from "../error/ErrorBoundaryPage";

// Local interface for modal display (mapped from API data)
interface SocialInsightDisplay {
    category: string;
    views: number;
    posts: number;
    dataInsight: string;
    audienceTrend: string;
    action: string;
    relevanceScore: string;
    relevanceAssessment: string;
}

const SocialInsightsPage = () => {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<SocialInsightDisplay | null>(null);
    const [channel, setChannel] = useState<string>("tiktok");

    const { socialInsightsData, isLoading, isError } = useSocialInsights();

    // Map API data to display format
    const mapToDisplay = (insight: SocialInsight): SocialInsightDisplay => ({
        category: insight.category,
        views: insight.views,
        posts: insight.posts,
        dataInsight: insight.dataInsights,
        audienceTrend: insight.audienceTrend,
        action: insight.action,
        relevanceScore: insight.relevanceAssessmentScore,
        relevanceAssessment: insight.relevanceAssessmentRationale
    });

    const handleCardClick = (insight: SocialInsight) => {
        setSelectedCategory(mapToDisplay(insight));
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedCategory(null);
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <CircularProgress />
            </Box>
        );
    }
    if (isError) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <Typography fontSize={36}>
                    Error loading data.
                </Typography>
            </Box>
        );
    }

    const handleChannelChange = (_event: React.MouseEvent<HTMLElement>, newChannel: string) => {
        if (newChannel !== null) {
            setChannel(newChannel);
        }
    }

    return (
        <>
            <ErrorBoundary fallback={<ErrorBoundaryPage/>}>

            <Box sx={{
                width: "100%",
                height: "100%",
                overflowY: "auto",
                boxSizing: "border-box",
                bgcolor: "#fafbfc",
                display: "flex",
                flexDirection: "column",
            }}>
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: { xs: "16px", sm: "24px", md: "32px" },
                    mt: "24px",
                    mb: "32px",
                    gap: 2,
                    maxWidth: "1800px",
                    width: "100%",
                    alignSelf: "center",
                    position: "relative",
                    zIndex: 10
                }}>
                    {/* <Typography sx={{ color: "#666", fontWeight: 500 }}>
                        {t("platform.viewBy")}
                    </Typography> */}
                    <ToggleButtonGroup
                        value={channel}
                        exclusive
                        onChange={handleChannelChange}
                        aria-label="Social media channel"
                        sx={{
                            backgroundColor: "white",
                            borderRadius: "25px",
                            gap: 0,
                            border: "solid 1px #DBDFE1",
                            "& .MuiToggleButtonGroup-grouped": {
                                border: "none !important",
                                borderRadius: "20px !important",
                                margin: "0 2px",
                                padding: "8px 24px",
                                fontWeight: 500,
                                textTransform: "none",
                                fontSize: "14px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                "&.Mui-selected": {
                                    backgroundColor: "#E87722 !important",
                                    color: "#ffffff !important",
                                    border: "none !important",
                                    "&:hover": {
                                        backgroundColor: "#E87722 !important",
                                    },
                                    "& svg": {
                                        fill: "#ffffff",
                                    },
                                },
                                "&:not(.Mui-selected)": {
                                    backgroundColor: "transparent",
                                    color: "black",
                                    border: "none !important",
                                    "&:hover": {
                                        backgroundColor: "#e0e0e0",
                                    },
                                    "& svg": {
                                        fill: "currentColor",
                                    },
                                },
                                "&.Mui-disabled": {
                                    backgroundColor: "#f5f5f5 !important",
                                    color: "#9e9e9e !important",
                                    opacity: 0.6,
                                    "& svg": {
                                        fill: "#9e9e9e",
                                    },
                                },
                            },
                        }}
                    >
                        <ToggleButton value="tiktok">
                            <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center" }}>
                                <TiktokIcon width={20} height={20} />
                            </Box>
                            TikTok
                        </ToggleButton>
                        <ToggleButton value="youtube" disabled>
                            <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center" }}>
                                <YoutubeIcon width={20} height={20} />
                            </Box>
                            YouTube
                        </ToggleButton>
                        <ToggleButton value="google" disabled>
                            <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center" }}>
                                <GoogleIcon width={20} height={20} />
                            </Box>
                            Google
                        </ToggleButton>
                        <ToggleButton value="meta" disabled>
                            <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center" }}>
                                <MetaIcon width={20} height={20} />
                            </Box>
                            Meta
                        </ToggleButton>

                    </ToggleButtonGroup>
                </Box>
                <Box sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "repeat(auto-fit, minmax(280px, 350px))",
                        sm: "repeat(auto-fit, minmax(280px, 350px))",
                        md: "repeat(3, 1fr)",
                        "@media (min-width: 2000px)": {
                            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 350px))"
                        }
                    },
                    gap: { xs: 2, sm: 3, md: 3 },
                    padding: { xs: "16px", sm: "24px", md: "32px" },
                    maxWidth: "1800px",
                    width: "100%",
                    justifyContent: "center",
                }}>
                    {socialInsightsData?.data?.map((insight: SocialInsight, index: number) => {
                        const isLastIndex = index === socialInsightsData?.data?.length - 1;
                        return (
                            <Box key={insight.category} sx={{ marginBottom: isLastIndex ? "36px" : 0 }}>
                                <SocialCategoryCard
                                    categoryData={insight}
                                    onClick={() => handleCardClick(insight)}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <CategoryModal
                open={isModalOpen}
                onClose={handleCloseModal}
                categoryData={selectedCategory}
            />
            </ErrorBoundary>
        </>
    )
}

export default SocialInsightsPage;